import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollReveal from '@/components/base/ScrollReveal';
import CountUp from '@/components/base/CountUp';
import ParticleBackground from '@/components/base/ParticleBackground';
import { useState } from 'react';

const modes = [
  {
    icon: 'ri-government-line',
    title: '政企项目合作',
    desc: '面向政府文旅单位，提供县域文化数字化建设、文旅IP孵化、文化数字资产库搭建等整体解决方案。',
    targets: '政府文旅局 · 宣传部 · 文保单位',
  },
  {
    icon: 'ri-building-line',
    title: '品牌赋能合作',
    desc: '面向品牌企业，提供品牌文化IP塑造、品牌故事定制、AI内容生产、流量电商运营等全链路赋能服务。',
    targets: '品牌商家 · 老字号 · 新消费品牌',
  },
  {
    icon: 'ri-team-line',
    title: '城市合伙人合作',
    desc: '面向各地有资源、有渠道的合作伙伴，开放城市合伙人体系，共享文化数字化产业红利。',
    targets: '区域代理 · 渠道合伙人 · 资源方',
  },
  {
    icon: 'ri-global-line',
    title: '内容生态合作',
    desc: '面向内容平台、MCN机构、创作者，开放AI内容生产平台与IP资源库，共建文化内容新生态。',
    targets: '内容平台 · MCN机构 · 创作者',
  },
  {
    icon: 'ri-community-line',
    title: 'OPC社区共建合作',
    desc: '联合地方政府与产业园区共建OPC社区，集聚AI人才、孵化创新项目、服务园区企业，形成区域文化数字产业协作网络。',
    targets: '地方政府 · 产业园区 · 社区运营方',
  },
];

const flow = [
  { step: '01', title: '在线咨询', desc: '提交合作意向' },
  { step: '02', title: '需求对接', desc: '专属顾问跟进' },
  { step: '03', title: '方案定制', desc: '量身定制方案' },
  { step: '04', title: '合作洽谈', desc: '深度沟通确认' },
  { step: '05', title: '签约落地', desc: '正式启动项目' },
  { step: '06', title: '项目交付', desc: '高品质交付' },
  { step: '07', title: '持续运维', desc: '长期服务保障' },
];

export default function Cooperation() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    setFormError('');

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="company_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9a6p2o81gss20ku8ut0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText) as Record<string, unknown>; } catch (_err) { parsed = {}; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message || (parsed?.message as string) || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch (_err) {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <div className="ui-page">
      <Navbar />
      <main className="pt-20">
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-background-900/40 to-transparent z-[1]"></div>
          <ParticleBackground compact />
          <div className="w-full px-4 md:px-8 lg:px-12 relative z-10">
            <div className="max-w-screen-2xl mx-auto">
              <ScrollReveal>
                <div className="text-center mb-16">
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 border border-primary-500/20 mb-6">
                    <span className="w-1 h-1 rounded-full bg-primary-500"></span>
                    <span className="text-primary-300 text-xs font-medium tracking-wider whitespace-nowrap">Cooperation</span>
                  </div>
                  <h1 className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-primary-500 mb-6">招商合作</h1>
                  <p className="text-secondary-400 text-lg max-w-2xl mx-auto">依托文化数字资产、AI量产技术与联盟生态，开放全链路合作</p>
                </div>
              </ScrollReveal>

              {/* Stats */}
              <ScrollReveal>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
                  {[
                     { value: 5, suffix: '', label: '合作模式' },
                    { value: 5, suffix: '+', label: '合作对象类型' },
                    { value: 7, suffix: '', label: '标准化合作流程' },
                    { value: 24, suffix: 'h', label: '专属顾问响应' },
                  ].map((stat) => (
                    <div key={stat.label} className="group bg-background-900/50 rounded-2xl border border-primary-500/10 p-6 text-center hover:border-primary-500/25 hover:-translate-y-2 transition-all duration-500 hover:shadow-[0_0_30px_rgba(180,140,80,0.12)]">
                      <div className="font-heading text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                        <CountUp end={stat.value} duration={2000} suffix={stat.suffix} />
                      </div>
                      <p className="text-secondary-400 text-xs md:text-sm">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </ScrollReveal>

              <ScrollReveal>
                <div className="group bg-background-900/50 rounded-2xl border border-primary-500/15 p-8 md:p-12 mb-16 text-center hover:border-primary-500/25 transition-all duration-500 hover:shadow-[0_0_30px_rgba(180,140,80,0.1)]">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-4 group-hover:text-primary-500 transition-colors duration-500">合作定位</h2>
                  <p className="text-secondary-300 text-base max-w-3xl mx-auto leading-relaxed">
                    晋铧叙梦以开放的姿态，诚邀<strong className="text-foreground-100 font-medium">政府文旅单位、品牌企业、渠道合伙人、文旅机构、院校团队</strong>等各类合作伙伴，共建文化数字化新生态，共享产业发展红利。
                  </p>
                </div>
              </ScrollReveal>

              <ScrollReveal>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-10 text-center">五大合作模式</h2>
              </ScrollReveal>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                {modes.map((item) => (
                  <ScrollReveal key={item.title}>
                    <div className="group bg-background-900/50 rounded-2xl border border-primary-500/10 p-6 md:p-8 hover:border-primary-500/25 hover:-translate-y-2 transition-all duration-500 hover:shadow-[0_0_30px_rgba(180,140,80,0.12)] h-full">
                      <div className="w-12 h-12 rounded-xl bg-accent-500/10 flex items-center justify-center mb-5 group-hover:bg-accent-500/20 group-hover:scale-110 transition-all duration-500">
                        <i className={`${item.icon} text-accent-500 text-xl`}></i>
                      </div>
                      <h3 className="text-foreground-50 font-semibold text-base mb-3 group-hover:text-accent-400 transition-colors duration-500">{item.title}</h3>
                      <p className="text-secondary-400 text-sm leading-relaxed mb-4">{item.desc}</p>
                      <div className="py-2 px-3 bg-secondary-500/10 rounded-lg border border-secondary-500/15 group-hover:border-secondary-500/25 transition-all duration-500">
                        <p className="text-secondary-400 text-xs">{item.targets}</p>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              <ScrollReveal>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-50 mb-10 text-center">合作流程</h2>
              </ScrollReveal>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-16">
                {flow.map((item) => (
                  <ScrollReveal key={item.step}>
                    <div className="group text-center relative">
                      <div className="w-14 h-14 mx-auto rounded-full bg-primary-500/10 border-2 border-primary-500/30 flex items-center justify-center mb-3 group-hover:bg-primary-500/20 group-hover:border-primary-500/50 group-hover:scale-110 group-hover:shadow-[0_0_15px_rgba(180,140,80,0.3)] transition-all duration-500">
                        <span className="font-heading text-primary-500 font-bold text-sm">{item.step}</span>
                      </div>
                      <h4 className="text-foreground-50 font-medium text-xs mb-1 group-hover:text-primary-500 transition-colors duration-300">{item.title}</h4>
                      <p className="text-secondary-500 text-[10px]">{item.desc}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>

              <ScrollReveal>
                <div className="group bg-background-900/50 rounded-2xl border border-primary-500/15 p-8 md:p-12 max-w-2xl mx-auto hover:border-primary-500/25 transition-all duration-500 hover:shadow-[0_0_30px_rgba(180,140,80,0.1)]">
                  <h2 className="font-heading text-2xl font-bold text-foreground-50 mb-2 text-center">提交合作意向</h2>
                  <p className="text-secondary-400 text-sm text-center mb-8">填写以下表单，我们的专属顾问将在24小时内与您联系</p>

                  {formStatus === 'success' ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 mx-auto rounded-full bg-primary-500/10 flex items-center justify-center mb-4">
                        <i className="ri-check-line text-3xl text-primary-500"></i>
                      </div>
                      <p className="text-foreground-50 font-semibold text-lg mb-2">提交成功！</p>
                      <p className="text-secondary-400 text-sm">感谢您的关注，我们将尽快与您联系。</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form="">
                      <div className="website-alt-field">
                        <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-secondary-300 text-sm mb-2">姓名 *</label>
                          <input type="text" name="name" required className="w-full px-4 py-3 bg-background-800 border border-primary-500/20 rounded-lg text-foreground-50 text-sm focus:border-primary-500/50 focus:outline-none transition-all duration-300" placeholder="请输入您的姓名" />
                        </div>
                        <div>
                          <label className="block text-secondary-300 text-sm mb-2">单位名称</label>
                          <input type="text" name="company" className="w-full px-4 py-3 bg-background-800 border border-primary-500/20 rounded-lg text-foreground-50 text-sm focus:border-primary-500/50 focus:outline-none transition-all duration-300" placeholder="请输入单位名称" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <label className="block text-secondary-300 text-sm mb-2">联系方式 *</label>
                          <input type="text" name="contact" required className="w-full px-4 py-3 bg-background-800 border border-primary-500/20 rounded-lg text-foreground-50 text-sm focus:border-primary-500/50 focus:outline-none transition-all duration-300" placeholder="手机号或邮箱" />
                        </div>
                        <div>
                          <label className="block text-secondary-300 text-sm mb-2">合作类型 *</label>
                          <select name="cooperation_type" required className="w-full px-4 py-3 bg-background-800 border border-primary-500/20 rounded-lg text-foreground-50 text-sm focus:border-primary-500/50 focus:outline-none transition-all duration-300 appearance-none cursor-pointer">
                            <option value="">请选择合作类型</option>
                            <option value="政企项目合作">政企项目合作</option>
                            <option value="品牌赋能合作">品牌赋能合作</option>
                            <option value="城市合伙人合作">城市合伙人合作</option>
                            <option value="内容生态合作">内容生态合作</option>
                            <option value="OPC社区共建合作">OPC社区共建合作</option>
                            <option value="产教融合合作">产教融合合作</option>
                            <option value="其他">其他</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label className="block text-secondary-300 text-sm mb-2">需求描述</label>
                        <textarea name="description" rows={4} maxLength={500} className="w-full px-4 py-3 bg-background-800 border border-primary-500/20 rounded-lg text-foreground-50 text-sm focus:border-primary-500/50 focus:outline-none transition-all duration-300 resize-none" placeholder="请简要描述您的合作需求（500字以内）"></textarea>
                        <p className="text-secondary-500 text-xs mt-1 text-right">最多500字</p>
                      </div>

                      {formStatus === 'error' && formError && (
                        <div className="px-4 py-3 bg-accent-500/10 border border-accent-500/20 rounded-lg">
                          <p className="text-accent-300 text-sm">{formError}</p>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus === 'submitting'}
                        className="w-full py-3.5 bg-accent-500 text-foreground-50 font-medium rounded-full text-sm hover:bg-accent-400 transition-all duration-300 disabled:opacity-60 whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                      >
                        {formStatus === 'submitting' ? (
                          <>
                            <div className="w-4 h-4 border-2 border-foreground-50/30 border-t-foreground-50 rounded-full animate-spin"></div>
                            提交中...
                          </>
                        ) : (
                          <>
                            提交意向
                            <div className="w-4 h-4 flex items-center justify-center">
                              <i className="ri-send-plane-line"></i>
                            </div>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
